import { Invite } from '@models/Core/Invite';
import { Idea } from '@models/Sessions/Idea/Idea';
import { Participant } from '@models/Sessions/Participant';
import { Session } from '@models/Sessions/Session';

import { IdeaSorting } from './idea-bar/idea-bar-controls/idea-bar-controls.component';
import { ParticipantSorting } from './modules/people-module/participant-bar/participant-bar-control/participant-bar-control.component';

const compareStrings = (a: string, b: string) => {
  if (a > b) {
    return 1;
  }
  if (a < b) {
    return -1;
  }
  return 0;
};

const status = (a: Idea) => {
  if (a.moves && a.moves.length > 0) {
    return 'Moved';
  }

  if (!a.published) {
    return 'Draft';
  }

  return '';
};

const originator = (a: Idea, s: Session) => {
  const participant = s.config.participants.find(
    p => p.user && p._id === a.created_by,
  );
  const user = participant ? participant.user : undefined;
  if (user) {
    return user.first_name + ' ' + user.name;
  } else {
    return a.created_by;
  }
};

export function compareIdeas(
  a: Idea,
  b: Idea,
  sorting: IdeaSorting,
  session: Session,
) {
  const dir = sorting.dir === 'asc' ? 1 : -1;

  if (sorting.field === 'title') {
    return compareStrings(a.title, b.title) * dir;
  } else if (sorting.field === 'status') {
    return compareStrings(status(a), status(b)) * dir;
  } else if (sorting.field === 'originator') {
    return compareStrings(originator(a, session), originator(b, session)) * dir;
  } else if (sorting.field === 'created_at') {
    return (
      ((new Date(a.created_at) as any) - (new Date(b.created_at) as any)) * dir
    );
  } else if (sorting.field === 'comments') {
    return (a.comments.length - b.comments.length) * dir;
  } else if (sorting.field === 'team') {
    return (a.collaborators.length - b.collaborators.length) * dir;
  } else if (sorting.field === 'followers') {
    return (a.followers.length - b.followers.length) * dir;
  }
  return null;
}

interface _ParticipantEntry {
  type: 'participant';
  entry: Participant;
}

interface _InviteEntry {
  type: 'invite';
  entry: Invite;
}

export type ParticipantEntry = _ParticipantEntry | _InviteEntry;

const name = (a: ParticipantEntry) => {
  if (a.type === 'invite') {
    return a.entry.first_name + ' ' + a.entry.name;
  } else {
    if (a.entry.user) {
      return a.entry.user.first_name + ' ' + a.entry.user.name;
    } else {
      return a.entry._id;
    }
  }
};

const org = (a: ParticipantEntry) => {
  if (a.type === 'invite') {
    return a.entry.organization || '';
  } else {
    if (a.entry.user) {
      return a.entry.user.organization || '';
    } else {
      return '';
    }
  }
};

export function participantEntryRole(a: ParticipantEntry, s: Session) {
  if (a.type === 'invite') {
    return a.entry.sessions.find(_s => _s.session === s._id).role;
  } else {
    return a.entry.role;
  }
}

export function participantIdeas(p: ParticipantEntry, s: Session) {
  if (p.type === 'invite') {
    return -1;
  } else {
    return s.data.ideas.filter(
      idea =>
        idea.created_by === p.entry._id ||
        idea.collaborators.some(
          collab => collab.participant_id === p.entry._id,
        ),
    ).length;
  }
}

export function participantComments(p: ParticipantEntry, s: Session) {
  if (p.type === 'invite') {
    return -1;
  } else {
    let comments = 0;
    s.data.ideas.forEach(idea => {
      idea.comments.forEach(comment => {
        if (comment.created_by === p.entry._id) {
          comments++;
        }
      });
    });

    return comments;
  }
}

const participantStatus = (p: ParticipantEntry) => {
  return p.type === 'invite' ? -1 : 1;
};

export function compareParticipants(
  a: ParticipantEntry,
  b: ParticipantEntry,
  sorting: ParticipantSorting,
  session: Session,
) {
  const dir = sorting.dir === 'asc' ? 1 : -1;

  if (sorting.field === 'name') {
    return compareStrings(name(a), name(b)) * dir;
  } else if (sorting.field === 'organization') {
    return compareStrings(org(a), org(b)) * dir;
  } else if (sorting.field === 'ideas') {
    return (participantIdeas(a, session) - participantIdeas(b, session)) * dir;
  } else if (sorting.field === 'comments') {
    return (
      (participantComments(a, session) - participantComments(b, session)) * dir
    );
  } else if (sorting.field === 'role') {
    return (
      (participantEntryRole(a, session) - participantEntryRole(b, session)) *
      dir
    );
  } else if (sorting.field === 'status') {
    return (participantStatus(a) - participantStatus(b)) * dir;
  }
  return null;
}
